import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface KeyboardShortcutsOptions {
  onEscape?: () => void;
  commandPaletteEnabled?: boolean;
}

export const useKeyboardShortcuts = (options: KeyboardShortcutsOptions = {}) => {
  const navigate = useNavigate();
  const { onEscape, commandPaletteEnabled = true } = options;

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Prevent shortcuts when user is typing in inputs
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement ||
      (e.target as HTMLElement)?.contentEditable === 'true'
    ) {
      return;
    }

    // Global command palette trigger
    if (commandPaletteEnabled && (e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      // TODO: Open command palette
      console.log('Command palette should open');
      return;
    }

    // Global keyboard shortcuts
    if ((e.metaKey || e.ctrlKey) && e.key === '/') {
      e.preventDefault();
      navigate('/support');
    }

    // Quick navigation shortcuts (Cmd/Ctrl + Shift + Key)
    if ((e.metaKey || e.ctrlKey) && e.shiftKey) {
      switch (e.key) {
        case 'H':
          e.preventDefault();
          navigate('/');
          break;
        case 'D':
          e.preventDefault();
          navigate('/dashboard');
          break;
        case 'B':
          e.preventDefault();
          navigate('/bills');
          break;
        case 'Q':
          e.preventDefault();
          navigate('/queue');
          break;
        case 'S':
          e.preventDefault();
          navigate('/settings');
          break;
        case 'P':
          e.preventDefault();
          navigate('/account/profile');
          break;
        case 'T':
          e.preventDefault();
          navigate('/workspace/team');
          break;
      }
    }

    // Escape key to close dropdowns/modals
    if (e.key === 'Escape') {
      if (onEscape) {
        e.preventDefault();
        onEscape();
        return;
      }
      // Close any open dropdowns by removing focus
      (document.activeElement as HTMLElement)?.blur();
    }
  }, [navigate, onEscape, commandPaletteEnabled]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return {
    triggerEscape: () => onEscape?.(),
  };
};